import { useEffect, useState } from "react";
import { TextField, Autocomplete, Box, Card, CardContent, CardMedia, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HiSparkles, HiEye } from "react-icons/hi2";
import AppPagination from "../Tools/AppPagination";
import api from "../../services/api";
import NewBikeDetailShort from "./NewBikeDetailShort";
import { useDispatch, useSelector } from "react-redux";
import { getBikeNames } from "../../State/NewBike/Action";

export default function NewBikeListShort() {
  const [bikes, setBikes] = useState([]);
  const [showBikeDetail, setShowBikeDetail] = useState(false);
  const [showBikeList, setShowBikeList] = useState(true);
  const [bikeId, setBikeId] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [model, setModel] = useState("");
  const navigate = useNavigate();

  const bikeNames = useSelector(
    (store) => store.newBikesResponse.bikeNames.bikeModelNames
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getBikeNames());

    loadBikes(page, model);
  }, [page, model]);

  const loadBikes = (pageNo, model) => {
    api.get(`/public/newBikes?model=${model}&page=${pageNo}`).then((res) => {
      setBikes(res.data.bikes);
      setTotalPages(res.data.totalPages);
    });
  };

  const handleViewMore = (e, bike) => {
    navigate(`/newBikeDetailShort?bikeId=${bike}`);
  };

  const handleBikeChange = (event, value) => {
    setModel(value?.id || "");
  };

  const defaultProps = {
    options: bikeNames,
    getOptionLabel: (option) => option.name,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {showBikeList && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Modern Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <HiSparkles className="w-8 h-8 text-accent-500 mr-3" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                New Bikes
              </h1>
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover the latest models with cutting-edge technology and premium features
            </p>
          </motion.div>

          {/* Modern Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex justify-center mb-8"
          >
            <Autocomplete
              onChange={handleBikeChange}
              onInputChange={(event, value, reason) => {
                if (reason === "clear") {
                  setModel("");
                }
              }}
              disablePortal
              {...defaultProps}
              sx={{ 
                width: 400, 
                backgroundColor: "white", 
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3b82f6",
                  },
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Search New Bikes" 
                  placeholder="Find your perfect ride..."
                />
              )}
            />
          </motion.div>

          {/* Modern Bike Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            {bikes.map((bike, index) => (
              <motion.div
                key={bike.bikeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group"
              >
                <Card 
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0"
                  sx={{ 
                    borderRadius: "16px",
                    "&:hover": {
                      boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                    }
                  }}
                >
                  <div className="relative overflow-hidden">
                    <CardMedia
                      component="img"
                      height="240"
                      image={bike.imageUrl}
                      alt={bike.brand + " " + bike.model}
                      className="cursor-pointer transition-transform duration-300 group-hover:scale-110"
                      onClick={(e) => handleViewMore(e, bike.bikeId)}
                      sx={{
                        width: "100%",
                        height: "240px",
                        objectFit: "contain",
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                      <HiSparkles className="w-5 h-5 text-accent-500" />
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <Typography 
                      variant="h6" 
                      className="font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      {bike.brand} {bike.model}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      className="text-gray-500 mb-3"
                      sx={{ fontSize: "0.9rem" }}
                    >
                      {bike.engineCapacity} CC Engine
                    </Typography>
                    
                    <div className="flex items-center justify-between mb-4">
                      <Typography 
                        variant="h6" 
                        className="font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent"
                        sx={{ fontSize: "1.2rem" }}
                      >
                        ₹{bike.exShowroomPrice.toLocaleString()}/-
                      </Typography>
                    </div>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={(e) => handleViewMore(e, bike.bikeId)}
                      className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl py-3 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      startIcon={<HiEye className="w-5 h-5" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        py: 1.5
                      }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Modern Pagination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex justify-center mt-12"
          >
            <Box className="bg-white rounded-2xl shadow-lg p-4">
              <AppPagination totalPages={totalPages} setPage={setPage} />
            </Box>
          </motion.div>
        </div>
      )}
      {showBikeDetail && <NewBikeDetailShort bikeId={bikeId} />}
    </div>
  );
}
